// Countdowns for timed quizzes, tasks and missions — CCT-288.
//
// ⚠️ THE DEADLINE COMES FROM THE SERVER, AND IT IS AN INSTANT. Nothing here counts seconds on
// its own: it subtracts `deadlineAt` from the current time. A duration handed to the client
// would restart at every reload, and F5 would be unlimited time.
//
// ⚠️ AND THIS IS A DISPLAY, NOT A RULE. The verdict belongs to the server, which re-checks the
// deadline when the item is submitted. Whatever this file draws, a late answer is refused with
// HTTP 409 `TIME_LIMIT_EXCEEDED`. Treating the countdown as the authority would make a browser
// console a way to buy time.
import { apiFetch } from './client';

export type TimeoutScope = 'fail_task' | 'fail_mission';

export interface MissionTimer {
  /** `null` for the mission-wide timer. */
  elementId: string | null;
  scope: 'mission' | 'element';
  limitSeconds: number;
  onTimeout: TimeoutScope;
  /** `null` while running, then `expired` or `done`. */
  outcome: 'expired' | 'done' | null;
  /** ISO 8601 instant WITH offset — the only value worth counting from. */
  deadlineAt: string | null;
  /** Server-side snapshot, useful for a first paint. NOT what we count down from. */
  remainingSeconds: number;
}

/**
 * Arms an element's timer when it opens. Idempotent server-side: re-opening a quiz does not
 * hand out fresh time.
 *
 * Returns `null` when the element carries no limit — the common case, and not an error.
 */
export async function startItemTimer(
  token: string | null, missionId: number, itemId: string,
): Promise<MissionTimer | null> {
  try {
    const r = await apiFetch(`/me/stories/${missionId}/items/${encodeURIComponent(itemId)}/timer`,
      { method: 'POST', token, auth: true });
    return (r?.timer as MissionTimer) ?? null;
  } catch {
    // ⚠️ NEVER BLOCK THE ELEMENT ON THIS. Failing to arm a countdown must not keep the player
    // out of their quiz; the server still holds the truth, and an unarmed timer simply never
    // expires. The reverse — refusing entry — would turn a display glitch into a lockout.
    return null;
  }
}

/**
 * The player HANDED IN: the clock stops.
 *
 * ⚠️ NOT THE SAME EVENT AS "completed". The app records an element as completed when it is
 * DISPLAYED (see `useElementVisited`), so that fires on open — stopping the timer there would
 * stop it the second it starts. This is the hand-in: quiz validated, task delivered.
 *
 * Called late it rescues nothing: the server only marks "done" while the deadline still holds.
 */
export async function stopItemTimer(
  token: string | null, missionId: number, itemId: string,
): Promise<MissionTimer | null> {
  try {
    const r = await apiFetch(`/me/stories/${missionId}/items/${encodeURIComponent(itemId)}/timer/stop`,
      { method: 'POST', token, auth: true });
    return (r?.timer as MissionTimer) ?? null;
  } catch {
    return null;
  }
}

/** Every timer of the run, expired ones included. */
export async function fetchMissionTimers(
  token: string | null, missionId: number,
): Promise<MissionTimer[]> {
  try {
    const r = await apiFetch(`/me/stories/${missionId}/timers`, { token, auth: true });
    return Array.isArray(r?.timers) ? (r.timers as MissionTimer[]) : [];
  } catch {
    return [];
  }
}

// The PURE functions live in `utils/missionTimerMath` — a module with no imports at all, hence
// loadable by the Node test harness. They are re-exported here so screens keep a single entry
// point.
export {
  secondesRestantes, formatChrono, chronoAAfficher, estDepassementDeDelai,
} from '../utils/missionTimerMath';
